/**
 * Reporting periods as `(year, month)` integers.
 *
 * The legacy stored these as text (`'2026-03'`), which blocked every range query
 * the reporting screens need — "all open periods before March" became a string
 * comparison. Integers sort and compare natively.
 */
import { JERUSALEM } from "../ports/clock.js";

export interface Period {
  year: number;
  /** 1-12. */
  month: number;
}

export function periodFromDate(date: Date, timeZone: string = JERUSALEM): Period {
  // Formatting in the business timezone matters: a submission just before
  // midnight on the 1st in Jerusalem is a different period than in UTC.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);

  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  return { year, month };
}

export function comparePeriods(a: Period, b: Period): number {
  return a.year - b.year || a.month - b.month;
}

export function addMonths(period: Period, delta: number): Period {
  const zeroBased = period.year * 12 + (period.month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

/** Sortable key for grouping and map lookups. */
export function periodKey(period: Period): string {
  return `${period.year}-${String(period.month).padStart(2, "0")}`;
}

export function parsePeriodKey(key: string): Period {
  const [year, month] = key.split("-");
  return { year: Number(year), month: Number(month) };
}

/** "מרץ 2026", matching the period pill in the hero surface. */
export function formatPeriod(period: Period): string {
  const month = new Intl.DateTimeFormat("he-IL", { month: "long" }).format(
    new Date(Date.UTC(period.year, period.month - 1, 15)),
  );
  return `${month} ${period.year}`;
}

/** "מרץ", for dense column headers where the year is already established. */
/**
 * The Hebrew month name.
 *
 * Takes a month number as well as a Period, because most call sites have only the
 * month — the year does not change the name, and requiring one produced
 * `{ year: 0, month }` placeholders at the call sites.
 */
export function formatMonthName(period: Period | number): string {
  const month = typeof period === "number" ? period : period.month;
  const year = typeof period === "number" ? 2000 : period.year;
  return new Intl.DateTimeFormat("he-IL", { month: "long" }).format(
    new Date(Date.UTC(year, month - 1, 15)),
  );
}

/**
 * Bi-monthly VAT filers report in odd months only, covering the current month
 * and the one before it. This is an Israeli filing rule, not a formatting
 * nicety, and getting it wrong means either a missed deadline or a spurious
 * document request — so it lives here and is unit-tested in @onecpa/domain.
 */
export function isBiMonthlyReportingMonth(period: Period): boolean {
  return period.month % 2 === 1;
}
